// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import {
  getCart,
  updateProductAndGetCartOrNotify,
  useDiscountDetails,
  removeProduct,
  type DecoratedProduct,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Fqdn } from '@gandi/ui';

import ProductItemBase from './components/ProductItemBase';
import {
  DetailRow,
  DiscountRow,
  DurationRow,
  TitleRow,
  TotalRow,
} from './components/Rows';

import type { GandiFormSelectOption } from './types';

type Props = {
  product: DecoratedProduct,
};

export const DNS_DURATION_ONE_MONTH = 1;
export const DNS_DURATION_ONE_YEAR = 12;

const DNS_VALUES: Array<number> = [
  DNS_DURATION_ONE_MONTH,
  DNS_DURATION_ONE_YEAR,
];

function PremiumDNSCreate({ product }: Props): React.MixedElement {
  const { fqdn, unitPrice, hasTaxes, id } = product;
  const { __ } = useTranslator();

  const dispatch = useDispatch();

  const currentPrices = product.prices?.[0];

  const discountDetails = useDiscountDetails({ product });

  const handleDurationChange: (
    selectOption: GandiFormSelectOption<string>,
  ) => void = (selectedOption: GandiFormSelectOption<string>) => {
    const newPayload = {
      ...product.payload,
      duration: parseInt(selectedOption.value, 10),
    };

    dispatch(updateProductAndGetCartOrNotify(__, product.id, newPayload));
  };

  const removeFromCart = async (e: any) => {
    e.preventDefault();
    try {
      await dispatch(removeProduct(product.id));
      dispatch(getCart());
    } catch (err) {
      dispatch(
        notifyDanger({
          message: __('An error has occurred. Please try again.'),
        }),
      );
    }
  };

  // $FlowFixMe[prop-missing]
  const currentDurationValue: number = currentPrices?.duration || 1;

  return (
    <ProductItemBase>
      <TitleRow
        label={__('Pack DNS Security+')}
        productType={product.productType}
        handleRemoveAction={removeFromCart}
      />
      <DurationRow
        value={currentDurationValue}
        values={DNS_VALUES}
        unit="m"
        disabledOption={!!product.isWorking}
        // $FlowFixMe[incompatible-type-arg] - number is number|string
        onChange={handleDurationChange}
        productId={product.id}
        total={product.isWorking ? undefined : unitPrice}
      />
      <DetailRow
        price={unitPrice}
        label={
          <>
            {__('For')} <Fqdn>{fqdn || ''}</Fqdn>
          </>
        }
      />
      {discountDetails?.map(discount => (
        <DiscountRow
          key={`discount-${id}-${discount.label}`}
          label={discount.label}
          // $FlowFixMe[incompatible-type] - price can be a string
          price={discount.price}
        />
      ))}
      <TotalRow
        hasTaxes={hasTaxes}
        totalPrice={
          product.computedTotalPrices?.beforeTaxes ??
          product.total?.before_taxes
        }
        hasDiscount={!!discountDetails?.length}
      />
    </ProductItemBase>
  );
}

export default PremiumDNSCreate;
